/**
 * The Application Designer command bar.
 *
 * Out of design mode it is just the Design button. In design mode it explains
 * what the sitting affects and carries the session verbs: Undo, Redo, Restore
 * default, Cancel, Save. Every designable page gets the same bar, so the verbs
 * mean the same thing on Patient Summary as on Appointment Booking.
 *
 * Restore default deliberately clears the undo stack — it replaces the draft
 * rather than editing it, so stepping back into a half-edited earlier state
 * would be misleading.
 */
import { Eye, EyeOff, Redo2, Undo2 } from "lucide-react";
import { descriptorElementTitle } from "@/lib/applicationDesigner/pageDescriptor";
import DesignButton from "./DesignButton";
import { designerTestIds } from "./designerTestIds";

export default function ApplicationDesignerBar({
  session,
  testIds,
  bannerText,
  designLabel = "Design",
  saveLabel = "Save layout",
}) {
  const ids = designerTestIds(testIds);
  if (!session.canDesign) return null;

  if (!session.designing) {
    return (
      <div className="ad-bar">
        <DesignButton canDesign testId={ids.toggle} label={designLabel} onClick={session.start} />
      </div>
    );
  }

  return (
    <div className="ad-bar">
      <p data-testid={ids.banner} className="ad-bar__banner">
        {bannerText}
      </p>
      <div className="ad-bar__actions">
        <button
          type="button"
          data-testid={ids.undo}
          className="ad-bar__btn"
          disabled={!session.canUndo}
          aria-label="Undo"
          title="Undo"
          onClick={session.undo}
        >
          <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
        <button
          type="button"
          data-testid={ids.redo}
          className="ad-bar__btn"
          disabled={!session.canRedo}
          aria-label="Redo"
          title="Redo"
          onClick={session.redo}
        >
          <Redo2 className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
        <button
          type="button"
          data-testid={ids.reset}
          className="ad-bar__btn"
          onClick={session.restoreDefault}
        >
          Restore default
        </button>
        <button type="button" data-testid={ids.cancel} className="ad-bar__btn" onClick={session.cancel}>
          Cancel
        </button>
        <button
          type="button"
          data-testid={ids.save}
          className="ad-bar__btn is-primary"
          disabled={session.busy}
          onClick={session.save}
        >
          {saveLabel}
        </button>
      </div>
    </div>
  );
}

export function HiddenElementsTray({ session, testIds, title = "Hidden elements" }) {
  const ids = designerTestIds(testIds);
  if (!session.designing || session.hidden.length === 0) return null;
  return (
    <div data-testid={ids.hidden} className="ad-hidden">
      <h3>
        <EyeOff className="h-3.5 w-3.5" aria-hidden="true" />
        {title}
      </h3>
      <ul>
        {session.hidden.map((item) => (
          <li key={item.id}>
            <span>{descriptorElementTitle(session.descriptor, item.id)}</span>
            <button
              type="button"
              data-testid={`${ids.restore}-${item.id}`}
              className="ad-bar__btn"
              onClick={() => session.show(item.id)}
            >
              <Eye className="h-3.5 w-3.5" aria-hidden="true" />
              Restore
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
